import { useEffect, useState } from 'react';
import { Button } from "@/components/ui/button";
import { Check, Copy, RefreshCw } from "lucide-react";
import {
  WALLET_CONNECT_FAILURE_EVENT,
  type WalletConnectFailureDetail,
} from '@coti-io/coti-wallet-plugin/rainbowkit';
import { cotiTestnet } from '@/config/chains';

interface NetworkParamRowProps {
  label: string;
  value: string;
  copiedField: string | null;
  onCopy: (label: string, value: string) => void;
}

const NetworkParamRow = ({ label, value, copiedField, onCopy }: NetworkParamRowProps) => (
  <div className="flex items-center justify-between gap-2 py-2 border-b border-border/50 last:border-b-0">
    <div className="min-w-0 text-left">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm text-foreground font-mono break-all">{value}</p>
    </div>
    <button
      type="button"
      onClick={() => onCopy(label, value)}
      aria-label={`Copy ${label}`}
      className="flex-shrink-0 p-2 rounded-md hover:bg-foreground/10 text-muted-foreground hover:text-foreground transition-colors"
    >
      {copiedField === label
        ? <Check className="h-4 w-4 text-green-500" />
        : <Copy className="h-4 w-4" />}
    </button>
  </div>
);

/**
 * Shown when a Zerion WalletConnect attempt fails. Zerion only approves
 * sessions for networks it already knows, so a portal targeting COTI Testnet
 * gets a generic in-wallet error; this modal tells the user how to add the
 * network manually in Zerion and retry.
 */
const ZerionConnectHelpModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    const onFailure = (event: Event) => {
      const detail = (event as CustomEvent<WalletConnectFailureDetail>).detail;
      if (detail?.walletId !== 'zerion') return;
      setIsOpen(true);
    };
    window.addEventListener(WALLET_CONNECT_FAILURE_EVENT, onFailure);
    return () => window.removeEventListener(WALLET_CONNECT_FAILURE_EVENT, onFailure);
  }, []);

  if (!isOpen) return null;

  const handleCopy = async (label: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedField(label);
      setTimeout(() => setCopiedField(current => (current === label ? null : current)), 2000);
    } catch {
      // Clipboard unavailable (non-secure context); user can still read the value
    }
  };

  return (
    // Above RainbowKit's connect modal (z-index 2147483646) so the guidance
    // is visible over its generic error state.
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      style={{ zIndex: 2147483647 }}
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="zerion-help-title"
    >
      <div className="rounded-[24px] glass-panel p-6 md:p-8 max-w-md w-full text-center relative overflow-y-auto max-h-[90vh] shadow-2xl">
        <button
          onClick={() => setIsOpen(false)}
          aria-label="Close"
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
        >
          ✕
        </button>

        <h2 id="zerion-help-title" className="text-2xl font-bold text-foreground mb-2">
          Zerion needs the COTI network
        </h2>
        <p className="text-sm text-muted-foreground mb-6">
          Zerion couldn't connect because {cotiTestnet.name} isn't among its built-in
          networks. Add it manually in the Zerion app, then try connecting again.
        </p>

        <ol className="text-left text-sm text-foreground space-y-2 mb-6 list-decimal list-inside">
          <li>In Zerion, open <span className="font-semibold">Settings → Networks</span></li>
          <li>Tap <span className="font-semibold">Add network manually</span> and enter the details below</li>
          <li>Return here and tap Zerion to connect again</li>
        </ol>

        <div className="rounded-xl bg-muted/40 border border-border px-4 py-2 mb-6">
          <NetworkParamRow label="Network Name" value={cotiTestnet.name} copiedField={copiedField} onCopy={handleCopy} />
          <NetworkParamRow label="RPC URL" value={cotiTestnet.rpcUrls.default.http[0]} copiedField={copiedField} onCopy={handleCopy} />
          <NetworkParamRow label="Chain ID" value={String(cotiTestnet.id)} copiedField={copiedField} onCopy={handleCopy} />
          <NetworkParamRow label="Currency Symbol" value={cotiTestnet.nativeCurrency.symbol} copiedField={copiedField} onCopy={handleCopy} />
          <NetworkParamRow label="Block Explorer" value={cotiTestnet.blockExplorers?.default.url ?? ''} copiedField={copiedField} onCopy={handleCopy} />
        </div>

        <Button
          className="w-full btn-gradient-connect font-bold h-12"
          onClick={() => setIsOpen(false)}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Done — try connecting again
        </Button>

        <p className="text-xs text-muted-foreground mt-4">
          Or connect with MetaMask — it adds the COTI network automatically.
        </p>
      </div>
    </div>
  );
};

export default ZerionConnectHelpModal;
